import React, { useState, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";


const PdfToJpgConverter = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const { darkMode } = useTheme();
  const [fileName, setFileName] = useState('');
  const [images, setImages] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState('');
  const [dpi, setDpi] = useState(150);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    setError('');
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a valid PDF file.');
      return;
    }

    setPdfFile(file);
    setFileName(file.name.replace(/\.[^/.]+$/, "")); // Remove extension
    setImages([]);
    setCurrentPage(1);
  };

  const convertPdfToJpg = async () => {
    if (!pdfFile) {
      setError('Please upload a PDF file first.');
      return;
    }

    setIsConverting(true);
    setError('');
    setImages([]);

    try {
      // Dynamically import pdf.js
      const pdfjsLib = await import('pdfjs-dist/build/pdf');
      const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

      const arrayBuffer = await pdfFile.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument(arrayBuffer);
      const pdf = await loadingTask.promise;

      setTotalPages(pdf.numPages);
      
      const newImages = [];
      const scale = dpi / 96; // Adjust scale based on DPI (96 is standard screen DPI)

      for (let i = 1; i <= pdf.numPages; i++) {
        setCurrentPage(i);
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: scale });

        // Create canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Render PDF page into canvas context
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;
        
        // Convert canvas to JPG
        const imageData = canvas.toDataURL('image/jpeg', 0.95);
        newImages.push({
          url: imageData,
          pageNumber: i,
          width: canvas.width,
          height: canvas.height
        });
      }

      setImages(newImages);
    } catch (err) {
      setError('Error converting PDF to JPG. Please try again.');
      console.error('Conversion error:', err);
    } finally {
      setIsConverting(false);
    }
  };

  const downloadImage = (image) => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = `${fileName}_page_${image.pageNumber}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllImages = () => {
    images.forEach((image) => {
      downloadImage(image);
    });
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      <HeaderSection toolId="pdfToJpg" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden
">          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faFilePdf}
                                className="mr-3"
                            />
                            PDF to JPG Converter
                        </h1>
                        <p className="opacity-90">
                            Convert PDF files to JPG images with high quality
                        </p>
                    </div>
          <div className="space-y-6">
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 m-6">
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={triggerFileInput}
                className="bg-[#74D19C] hover:bg-[#74D19C] text-white font-medium py-2 px-4 rounded-md transition duration-200"
              >
                Upload PDF File
              </button>
              <p className="mt-2 text-sm text-gray-500">Supports .pdf files</p>
              {fileName && (
                <p className="mt-2 text-sm text-gray-700">
                  File: <span className="font-medium">{fileName}.pdf</span>
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 m-6 p-6">
              <div>
                <label htmlFor="dpi" className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-1">
                  Image Quality (DPI)
                </label>
                <select
                  id="dpi"
                  value={dpi}
                  onChange={(e) => setDpi(parseInt(e.target.value))}
                  className="w-full dark:bg-gray-700 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="72">Low (72 DPI)</option>
                  <option value="150">Medium (150 DPI)</option>
                  <option value="300">High (300 DPI)</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={convertPdfToJpg}
                  disabled={!pdfFile || isConverting}
                  className={`w-full py-2 px-4 rounded-md text-white font-medium ${!pdfFile ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} transition duration-200`}
                >
                  {isConverting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Converting {currentPage}/{totalPages}...
                    </span>
                  ) : (
                    'Convert to JPG'
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {images.length > 0 && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Converted Images</h2>
                  <button
                    onClick={downloadAllImages}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-1 px-3 rounded-md text-sm transition duration-200"
                  >
                    Download All
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {images.map((image) => (
                    <div key={image.pageNumber} className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
                      <div className="p-2 bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Page {image.pageNumber}</span>
                        <button
                          onClick={() => downloadImage(image)}
                          className="text-xs bg-green-500 hover:bg-green-600 text-white py-1 px-2 rounded transition duration-200"
                        >
                          Download
                        </button>
                      </div>
                      <div className="p-2 flex justify-center bg-gray-100">
                        <img 
                          src={image.url} 
                          alt={`Page ${image.pageNumber}`}
                          className="max-w-full h-auto border border-gray-200"
                          style={{ maxHeight: '200px' }}
                        />
                      </div>
                      <div className="p-2 bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 text-center">
                        {image.width} × {image.height} px
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <BottomSection toolId="pdfToJpg" />
    </>
  );
};

export default PdfToJpgConverter;